import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, query, orderBy, onSnapshot, setDoc, updateDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';
import { getDownloadURL, ref, uploadString } from 'firebase/storage';
import { db, auth, storage } from '../config/firebase';
import { Basis, Bit, QuantumKeyService } from '../services/quantumService';
import { EncryptionService } from '../services/encryptionService';
import { clearConversationMessages } from '../services/chatCleanupService';
import '../styles/Chat.css';

const QUANTUM_STEPS = ['Alice', 'Channel', 'Bob', 'Sifting', 'Key Ready'];
const CHAT_DEBUG = true;

const chatDebug = (...args: any[]) => {
  if (!CHAT_DEBUG) return;
  console.log('[ChatDebug]', ...args);
};

const maskKey = (key: string) => {
  if (!key) return 'none';
  return `${key.slice(0, 8)}...(${key.length})`;
};

const maskCipher = (cipher: string) => {
  if (!cipher) return 'empty';
  return `${cipher.slice(0, 18)}...(${cipher.length})`;
};

const looksLikeCiphertext = (value: string) => {
  if (!value || value.length < 16) return false;
  const base64Pattern = /^[A-Za-z0-9+/=]+$/;
  return base64Pattern.test(value) && value.length % 4 === 0 && !value.includes(' ');
};

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1024;
const IMAGE_COMPRESSION_QUALITY = 0.8;
const UPLOAD_TIMEOUT_MS = 20000;
const INLINE_IMAGE_FALLBACK_MAX = 700 * 1024;

type MessageType = 'text' | 'image';

interface ImageDraft {
  fileName: string;
  mimeType: string;
  originalSize: number;
  compressedWidth: number;
  compressedHeight: number;
  compressedDataUrl: string;
}

interface Message {
  id: string;
  text: string;
  senderId: string;
  senderName?: string;
  encrypted?: boolean;
  messageType?: MessageType;
  mimeType?: string;
  storagePath?: string;
  storageUrl?: string;
  hasInlineFallback?: boolean;
  conversationId?: string;
  clientMessageId?: string;
  timestamp: Timestamp | Date;
}

type MessageDocument = Record<string, any>;

interface ChatUser {
  id: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
}

const Chat: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatUser, setChatUser] = useState<ChatUser | null>(null);
  const [fetchingUser, setFetchingUser] = useState(!!userId);
  const [quantumLoading, setQuantumLoading] = useState(false);
  const [eavesdropperActive, setEavesdropperActive] = useState(false);
  const [quantumError, setQuantumError] = useState<string | null>(null);
  const [quantumStatus, setQuantumStatus] = useState('Idle');
  const [quantumStep, setQuantumStep] = useState(0);
  const [matchingCount, setMatchingCount] = useState(0);
  const [finalKeyHex, setFinalKeyHex] = useState('');
  const [imageSending, setImageSending] = useState(false);
  const [imageDraft, setImageDraft] = useState<ImageDraft | null>(null);
  const [decryptedImageMap, setDecryptedImageMap] = useState<Record<string, string>>({});
  const aliceDataRef = useRef<{ bits: Bit[]; bases: Basis[] } | null>(null);
  const abortedRedirectedRef = useRef(false);
  const backRedirectedRef = useRef(false);
  const nestedMessagesRef = useRef<Message[]>([]);
  const flatMessagesRef = useRef<Message[]>([]);
  const nestedListenerErrorRef = useRef(false);
  const flatListenerErrorRef = useRef(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  // Generate a consistent conversation ID based on two user IDs
  const getConversationId = (user1Id: string, user2Id: string) => {
    return [user1Id, user2Id].sort().join('_');
  };

  const getConversationParticipants = () => {
    if (!currentUser || !userId) return [];
    return [currentUser.uid, userId].sort();
  };

  const createClientMessageId = () => {
    if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }

    return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, message: string) => {
    let timeoutHandle: number | null = null;

    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutHandle = window.setTimeout(() => {
        reject(new Error(message));
      }, timeoutMs);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
      }
    }
  };

  const isStorageUploadBlocked = (error: unknown) => {
    if (!(error instanceof Error)) return false;
    const text = error.message.toLowerCase();
    return text.includes('cors')
      || text.includes('timed out')
      || text.includes('storage/unknown')
      || text.includes('network');
  };

  const readFileAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read image file.'));
        return;
      }

      resolve(result);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read image file.'));
    };

    reader.readAsDataURL(file);
  });

  const loadImage = (source: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image for compression.'));
    image.src = source;
  });

  const compressImageToDataUrl = async (file: File) => {
    const originalDataUrl = await readFileAsDataUrl(file);
    const image = await loadImage(originalDataUrl);

    const longestSide = Math.max(image.width, image.height);
    const scale = longestSide > MAX_IMAGE_DIMENSION
      ? MAX_IMAGE_DIMENSION / longestSide
      : 1;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas context unavailable for compression.');
    }

    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const targetMimeType = file.type === 'image/png' ? 'image/jpeg' : file.type;
    const compressedDataUrl = canvas.toDataURL(targetMimeType, IMAGE_COMPRESSION_QUALITY);

    return {
      originalDataUrl,
      compressedDataUrl,
      width: canvas.width,
      height: canvas.height,
    };
  };

  const getMessageTimestamp = (message: Message) => {
    if (message.timestamp instanceof Timestamp) {
      return message.timestamp.toMillis();
    }

    if (message.timestamp instanceof Date) {
      return message.timestamp.getTime();
    }

    return 0;
  };

  const conversationId = currentUser && userId ? getConversationId(currentUser.uid, userId) : null;

  const mergeMessages = useCallback((messageLists: Message[][]) => {
    const mergedMessages = new Map<string, Message>();

    messageLists.flat().forEach((message) => {
      const timestampValue = getMessageTimestamp(message);
      const dedupeKey = message.clientMessageId
        || `${message.conversationId || conversationId || ''}:${message.senderId}:${timestampValue}:${message.text}:${message.encrypted ? '1' : '0'}:${message.messageType || 'text'}`;

      if (!mergedMessages.has(dedupeKey)) {
        mergedMessages.set(dedupeKey, message);
      }
    });

    return Array.from(mergedMessages.values()).sort((left, right) => {
      const leftTimestamp = getMessageTimestamp(left);
      const rightTimestamp = getMessageTimestamp(right);

      if (leftTimestamp !== rightTimestamp) {
        return leftTimestamp - rightTimestamp;
      }

      return left.id.localeCompare(right.id);
    });
  }, [conversationId]);

  const normalizeMessage = (docId: string, messageData: MessageDocument): Message | null => {
    const rawText = messageData.text
      ?? messageData.message
      ?? messageData.content
      ?? messageData.body
      ?? messageData.ciphertext
      ?? messageData.messageText;

    if (typeof rawText !== 'string') {
      return null;
    }

    const rawTimestamp = messageData.timestamp
      ?? messageData.createdAt
      ?? messageData.sentAt
      ?? messageData.created_at
      ?? messageData.date;

    const timestamp = rawTimestamp instanceof Timestamp
      ? rawTimestamp
      : rawTimestamp instanceof Date
        ? rawTimestamp
        : typeof rawTimestamp?.toDate === 'function'
          ? rawTimestamp.toDate()
          : typeof rawTimestamp === 'number'
            ? new Date(rawTimestamp)
            : new Date();

    return {
      id: docId,
      text: rawText,
      senderId: messageData.senderId
        ?? messageData.fromId
        ?? messageData.authorId
        ?? messageData.uid
        ?? messageData.userId
        ?? messageData.user?._id
        ?? '',
      senderName: messageData.senderName
        ?? messageData.fromName
        ?? messageData.authorName
        ?? messageData.displayName
        ?? messageData.name
        ?? messageData.user?.name,
      messageType: (messageData.messageType ?? messageData.type ?? 'text') as MessageType,
      mimeType: messageData.mimeType,
      storagePath: messageData.storagePath,
      storageUrl: messageData.storageUrl,
      hasInlineFallback: Boolean(messageData.hasInlineFallback),
      encrypted: Boolean(
        messageData.encrypted
        ?? messageData.isEncrypted
        ?? messageData.secure
        ?? messageData.user?._id
        ?? looksLikeCiphertext(rawText)
      ),
      conversationId: messageData.conversationId
        ?? messageData.chatId
        ?? messageData.threadId
        ?? messageData.roomId
        ?? messageData.sessionId
        ?? messageData.channelId,
      clientMessageId: messageData.clientMessageId ?? messageData.messageId ?? messageData.id,
      timestamp,
    };
  };

  const resetLocalChatRuntime = useCallback(() => {
    nestedMessagesRef.current = [];
    flatMessagesRef.current = [];
    setMessages([]);
    setInput('');
    setFinalKeyHex('');
    setMatchingCount(0);
    setQuantumStep(0);
    setQuantumStatus('Idle');
    setQuantumError(null);
    aliceDataRef.current = null;
    setImageDraft(null);
    setDecryptedImageMap({});
  }, []);

  const buildMessageData = (payloadText: string, overrides: Record<string, any> = {}) => ({
    text: payloadText,
    createdAt: serverTimestamp(),
    user: {
      _id: currentUser?.uid,
      name: currentUser?.displayName || 'Anonymous',
    },
    senderId: currentUser?.uid,
    senderName: currentUser?.displayName || 'Anonymous',
    conversationId,
    chatId: conversationId,
    threadId: conversationId,
    roomId: conversationId,
    sessionId: conversationId,
    participants: getConversationParticipants(),
    memberIds: getConversationParticipants(),
    userIds: getConversationParticipants(),
    receiverId: userId,
    recipientId: userId,
    toId: userId,
    clientMessageId: createClientMessageId(),
    timestamp: Timestamp.now(),
    ...overrides,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthResolved(true);
    });

    return () => unsubscribe();
  }, []);

  // Fetch the chat user and set up real-time listener
  useEffect(() => {
    if (!userId) return;

    const fetchChatUser = async () => {
      try {
        setFetchingUser(true);
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setChatUser({
            id: userSnap.id,
            ...userSnap.data() as any,
          });
        } else {
          setChatUser(null);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setChatUser(null);
      } finally {
        setFetchingUser(false);
      }
    };

    fetchChatUser();
  }, [userId]);

  // Set up real-time listener for messages
  useEffect(() => {
    if (!conversationId || !currentUser) return;

    nestedMessagesRef.current = [];
    flatMessagesRef.current = [];
    nestedListenerErrorRef.current = false;
    flatListenerErrorRef.current = false;

    const syncMessages = () => {
      setMessages(mergeMessages([nestedMessagesRef.current, flatMessagesRef.current]));

      if (nestedListenerErrorRef.current && flatListenerErrorRef.current) {
        setChatError('Unable to load messages. Check Firestore rules and authentication.');
      } else {
        setChatError(null);
      }
    };

    try {
      const sessionMessagesRef = collection(db, 'sessions', conversationId, 'messages');
      const sessionQuery = query(sessionMessagesRef, orderBy('createdAt', 'asc'));

      const sessionUnsubscribe = onSnapshot(sessionQuery, (snapshot) => {
        nestedMessagesRef.current = [];
        snapshot.forEach((docSnapshot) => {
          const normalizedMessage = normalizeMessage(docSnapshot.id, docSnapshot.data() as MessageDocument);
          if (normalizedMessage) {
            nestedMessagesRef.current.push(normalizedMessage);
          }
        });
        nestedListenerErrorRef.current = false;
        syncMessages();
      }, (error) => {
        console.error('Session listener error:', error);
        nestedListenerErrorRef.current = true;
        syncMessages();
      });

      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'asc'));

      const nestedUnsubscribe = onSnapshot(q, (snapshot) => {
        flatMessagesRef.current = [];
        snapshot.forEach((docSnapshot) => {
          const normalizedMessage = normalizeMessage(docSnapshot.id, docSnapshot.data() as MessageDocument);
          if (normalizedMessage) {
            flatMessagesRef.current.push(normalizedMessage);
          }
        });
        flatListenerErrorRef.current = false;
        syncMessages();
      }, (error) => {
        console.error('Realtime listener error:', error);
        flatListenerErrorRef.current = true;
        syncMessages();
      });

      return () => {
        sessionUnsubscribe();
        nestedUnsubscribe();
      };
    } catch (error) {
      console.error('Error setting up message listener:', error);
    }
  }, [conversationId, currentUser, mergeMessages]);

  // Watch the handshake session for abort and real-time BB84 key synchronization.
  useEffect(() => {
    if (!currentUser || !userId) return;

    abortedRedirectedRef.current = false;
    const sessionId = getConversationId(currentUser.uid, userId);
    const sessionRef = doc(db, 'sessions', sessionId);

    const unsubscribe = onSnapshot(sessionRef, async (snapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data() as {
        status?: string;
        abortedAt?: string | null;
        backInitiatedAt?: string | null;
        aliceId?: string | null;
        quantumPayload?: number[] | null;
        bobBases?: Basis[] | null;
        matchingIndexes?: number[] | null;
        handshakeComplete?: boolean;
      };

      const wasAborted = data.status === 'initializing' && !!data.abortedAt;
      if (wasAborted && !abortedRedirectedRef.current) {
        abortedRedirectedRef.current = true;
        resetLocalChatRuntime();
        setChatError('Quantum session aborted. Previous chat cleared. Redirecting to users...');
        navigate('/users');
        return;
      }

      const backInitiated = !!data.backInitiatedAt;
      if (backInitiated && !backRedirectedRef.current) {
        backRedirectedRef.current = true;
        resetLocalChatRuntime();
        setChatError('Peer returned to users. Chat cleared. Redirecting...');
        navigate('/users');
        return;
      }

      // Bob auto-generates random measurement bases when Alice starts rekey.
      if (
        data.status === 'rekeying'
        && Array.isArray(data.quantumPayload)
        && data.aliceId !== currentUser.uid
        && !Array.isArray(data.bobBases)
      ) {
        const bobBases: Basis[] = data.quantumPayload.map(() => (Math.random() > 0.5 ? 'X' : '+'));
        await updateDoc(sessionRef, { bobBases });
      }

      // Alice performs sifting after Bob publishes bases.
      if (
        data.status === 'rekeying'
        && Array.isArray(data.bobBases)
        && data.aliceId === currentUser.uid
        && !Array.isArray(data.matchingIndexes)
        && aliceDataRef.current
      ) {
        const matchingIndexes = aliceDataRef.current.bases
          .map((basis, index) => (basis === data.bobBases?.[index] ? index : null))
          .filter((index): index is number => index !== null);

        await updateDoc(sessionRef, {
          matchingIndexes,
          handshakeComplete: true,
          status: 'secure',
        });
      }

      // Both sides derive the exact same key from session data.
      if (data.handshakeComplete && Array.isArray(data.matchingIndexes) && Array.isArray(data.quantumPayload)) {
        const siftedBits: Bit[] = data.matchingIndexes
          .map((idx) => data.quantumPayload?.[idx])
          .filter((bit): bit is number => bit === 0 || bit === 1)
          .map((bit) => (bit === 1 ? 1 : 0));

        const derivedKey = QuantumKeyService.formatToHex(siftedBits);
        chatDebug('Handshake complete', {
          sessionId,
          matchingBits: siftedBits.length,
          matchingIndexes: data.matchingIndexes.length,
          quantumPayloadSize: data.quantumPayload.length,
          derivedKey: maskKey(derivedKey),
          actor: currentUser.uid === data.aliceId ? 'alice' : 'bob',
        });

        setMatchingCount(siftedBits.length);
        setFinalKeyHex(derivedKey);
        setQuantumStep(5);
        setQuantumStatus('Secure key synchronized in real time.');
        setQuantumLoading(false);
        setQuantumError(null);
      } else if (data.status === 'rekeying') {
        if (data.aliceId === currentUser.uid) {
          setQuantumStep(2);
          setQuantumStatus('Alice: waiting for Bob measurement bases...');
        } else {
          setQuantumStep(3);
          setQuantumStatus('Bob: measuring photons and publishing bases...');
        }
      }
    });

    return () => unsubscribe();
  }, [currentUser, userId, navigate, resetLocalChatRuntime]);

  const handleSendMessage = async () => {
    if (!input.trim() || !conversationId || !currentUser) return;

    setLoading(true);
    try {
      const shouldEncrypt = finalKeyHex.length > 0;
      const payloadText = shouldEncrypt
        ? EncryptionService.encrypt(input, finalKeyHex)
        : input;
      const messageData = buildMessageData(payloadText, {
        encrypted: shouldEncrypt,
        messageType: 'text',
      });

      chatDebug('Sending message', {
        conversationId,
        encrypted: shouldEncrypt,
        key: maskKey(finalKeyHex),
        plainLength: input.length,
        cipherPreview: shouldEncrypt ? maskCipher(payloadText) : 'not-encrypted',
      });

      await Promise.all([
        addDoc(collection(db, 'sessions', conversationId, 'messages'), messageData),
        addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData),
      ]);
      setInput('');
      setChatError(null);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatError('Failed to send message. Check Firestore rules and try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendImageMessage = async () => {
    if (!conversationId || !currentUser || !imageDraft) return;

    if (!finalKeyHex) {
      setChatError('Run the key exchange before sending images.');
      return;
    }

    setImageSending(true);
    setChatError(null);

    try {
      const sessionRef = doc(db, 'sessions', conversationId);
      await setDoc(sessionRef, {
        participants: getConversationParticipants(),
      }, { merge: true });

      if (imageDraft.compressedDataUrl.length > MAX_IMAGE_BYTES) {
        setChatError('Compressed image is too large to upload. Please choose a smaller photo.');
        return;
      }

      const encryptedImageText = EncryptionService.encrypt(imageDraft.compressedDataUrl, finalKeyHex);
      const storagePath = `encrypted-images/${conversationId}/${Date.now()}_${createClientMessageId()}.enc`;
      const storageRef = ref(storage, storagePath);

      let messageData: Record<string, any> | null = null;
      const inlineCiphertext = encryptedImageText.length <= INLINE_IMAGE_FALLBACK_MAX
        ? encryptedImageText
        : '[encrypted-image-storage]';
      const hasInlineFallback = inlineCiphertext !== '[encrypted-image-storage]';

      try {
        await withTimeout(
          uploadString(storageRef, encryptedImageText, 'raw', {
            contentType: 'text/plain',
          }),
          UPLOAD_TIMEOUT_MS,
          'Upload timed out. Check internet or Storage rules.',
        );

        messageData = buildMessageData(inlineCiphertext, {
          encrypted: true,
          messageType: 'image',
          mimeType: imageDraft.mimeType,
          fileName: imageDraft.fileName,
          fileSize: imageDraft.originalSize,
          compressedWidth: imageDraft.compressedWidth,
          compressedHeight: imageDraft.compressedHeight,
          storagePath,
          hasInlineFallback,
        });
      } catch (uploadError) {
        if (!isStorageUploadBlocked(uploadError)) {
          throw uploadError;
        }

        // Fallback path keeps chat usable while Storage CORS is being fixed.
        if (encryptedImageText.length > INLINE_IMAGE_FALLBACK_MAX) {
          throw new Error('Storage upload blocked by CORS and inline fallback payload is too large.');
        }

        messageData = buildMessageData(encryptedImageText, {
          encrypted: true,
          messageType: 'image',
          mimeType: imageDraft.mimeType,
          fileName: imageDraft.fileName,
          fileSize: imageDraft.originalSize,
          compressedWidth: imageDraft.compressedWidth,
          compressedHeight: imageDraft.compressedHeight,
          storagePath: null,
          hasInlineFallback: true,
        });

        setChatError('Storage upload blocked (CORS). Sent using inline encrypted fallback.');
      }

      chatDebug('Sending image', {
        conversationId,
        key: maskKey(finalKeyHex),
        fileName: imageDraft.fileName,
        fileSize: imageDraft.originalSize,
        mimeType: imageDraft.mimeType,
        compressedWidth: imageDraft.compressedWidth,
        compressedHeight: imageDraft.compressedHeight,
        storagePath: messageData.storagePath || 'inline-fallback',
        cipherPreview: maskCipher(encryptedImageText),
      });

      await Promise.all([
        addDoc(collection(db, 'sessions', conversationId, 'messages'), messageData),
        addDoc(collection(db, 'conversations', conversationId, 'messages'), messageData),
      ]);
      setImageDraft(null);
    } catch (error) {
      console.error('Error sending image:', error);
      const reason = error instanceof Error ? error.message : 'Unknown error';
      setChatError(`Failed to send image: ${reason}`);
    } finally {
      setImageSending(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const handleImageButtonClick = () => {
    imageInputRef.current?.click();
  };

  const handleImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setChatError('Only image files are supported.');
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setChatError('Image is too large. Please choose an image under 5 MB.');
      return;
    }

    setChatError(null);

    try {
      const compressionResult = await compressImageToDataUrl(file);
      setImageDraft({
        fileName: file.name,
        mimeType: file.type,
        originalSize: file.size,
        compressedWidth: compressionResult.width,
        compressedHeight: compressionResult.height,
        compressedDataUrl: compressionResult.compressedDataUrl,
      });
    } catch (error) {
      console.error('Error preparing image preview:', error);
      setChatError('Failed to prepare image preview. Try a different image.');
      setImageDraft(null);
    } finally {
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const clearImageDraft = () => {
    setImageDraft(null);
    setChatError(null);
  };

  const handleBackClick = async () => {
    if (!conversationId || !currentUser || !userId) {
      navigate('/users');
      return;
    }

    const sessionId = getConversationId(currentUser.uid, userId);
    const sessionRef = doc(db, 'sessions', sessionId);

    try {
      await clearConversationMessages(conversationId);
      resetLocalChatRuntime();

      await setDoc(
        sessionRef,
        {
          backInitiatedAt: new Date().toISOString(),
        },
        { merge: true },
      );
    } catch (error) {
      console.error('Back button cleanup failed:', error);
    } finally {
      navigate('/users');
    }
  };

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const runQuantumHandshake = async () => {
    if (!currentUser || !userId) return;

    setQuantumLoading(true);
    setQuantumError(null);
    setFinalKeyHex('');
    setMatchingCount(0);
    setQuantumStep(1);
    setQuantumStatus('Preparing BB84 transmission...');
    await wait(500);
    setQuantumStep(2);
    setQuantumStatus('Publishing photons to shared session...');

    const result = await QuantumKeyService.generateAndTransmit(256, eavesdropperActive);

    if (!result.success) {
      setQuantumError(result.error || 'Quantum channel failed.');
      setQuantumStatus('Quantum channel failed');
      setFinalKeyHex('');
      setMatchingCount(0);
      setQuantumStep(0);
      setQuantumLoading(false);
      return;
    }

    aliceDataRef.current = {
      bits: result.aliceBits,
      bases: result.aliceBases,
    };

    const sessionId = getConversationId(currentUser.uid, userId);
    const sessionRef = doc(db, 'sessions', sessionId);

    await setDoc(
      sessionRef,
      {
        participants: getConversationParticipants(),
        handshakeComplete: false,
        quantumPayload: result.photonsForBob,
        bobBases: null,
        matchingIndexes: null,
        aliceId: currentUser.uid,
        status: 'rekeying',
        abortedAt: null,
        abortedBy: null,
      },
      { merge: true },
    );

    setQuantumStep(3);
    setQuantumStatus('Waiting for peer to complete BB84 measurement...');
  };

  const abortQuantumHandshake = async () => {
    if (!currentUser || !userId || quantumLoading) return;

    const sessionId = getConversationId(currentUser.uid, userId);
    const sessionRef = doc(db, 'sessions', sessionId);

    setQuantumLoading(true);
    setQuantumError(null);
    setChatError(null);
    clearImageDraft();

    try {
      await clearConversationMessages(sessionId);
      resetLocalChatRuntime();

      await setDoc(
        sessionRef,
        {
          handshakeComplete: false,
          quantumPayload: null,
          bobBases: null,
          matchingIndexes: null,
          aliceId: null,
          status: 'initializing',
          abortedAt: new Date().toISOString(),
          abortedBy: currentUser.uid,
        },
        { merge: true },
      );

      setChatError('Chat aborted. All previous messages were deleted.');
      navigate('/users');
    } catch (error) {
      console.error('Abort cleanup failed:', error);
      setQuantumError('Failed to clear previous chat history. Please retry abort.');
    } finally {
      setQuantumLoading(false);
    }
  };

  const getMessageText = (msg: Message): string => {
    const shouldTryDecrypt = Boolean(finalKeyHex) && (
      Boolean(msg.encrypted) || msg.messageType === 'image' || looksLikeCiphertext(msg.text)
    );

    if (!shouldTryDecrypt) {
      return msg.text;
    }

    const decrypted = EncryptionService.decrypt(msg.text, finalKeyHex);
    const decryptionFailed = !decrypted || decrypted.includes('Decryption Error');

    chatDebug('Decrypt attempt', {
      messageId: msg.id,
      senderId: msg.senderId,
      key: maskKey(finalKeyHex),
      cipherPreview: maskCipher(msg.text),
      success: !decryptionFailed,
      outputPreview: decryptionFailed
        ? String(decrypted)
        : `${String(decrypted).slice(0, 32)}...`,
    });

    if (decryptionFailed) {
      return msg.encrypted ? '[Encrypted] Unable to decrypt with current key.' : msg.text;
    }

    return decrypted;
  };

  const getMessageImageSource = (msg: Message): string | null => {
    if (msg.messageType !== 'image') return null;

    if (msg.storagePath || msg.storageUrl) {
      if (decryptedImageMap[msg.id]) {
        return decryptedImageMap[msg.id];
      }

      if (msg.hasInlineFallback && msg.text && msg.text !== '[encrypted-image-storage]') {
        const inlineDecrypted = EncryptionService.decrypt(msg.text, finalKeyHex);
        if (inlineDecrypted && !inlineDecrypted.includes('Decryption Error')) {
          if (inlineDecrypted.startsWith('data:image/')) {
            return inlineDecrypted;
          }

          if (msg.mimeType) {
            return `data:${msg.mimeType};base64,${inlineDecrypted}`;
          }

          return `data:image/png;base64,${inlineDecrypted}`;
        }
      }

      return null;
    }

    const decrypted = getMessageText(msg);
    if (!decrypted || decrypted.startsWith('[Encrypted]')) {
      return null;
    }

    if (decrypted.startsWith('data:image/')) {
      return decrypted;
    }

    if (msg.mimeType) {
      return `data:${msg.mimeType};base64,${decrypted}`;
    }

    return `data:image/png;base64,${decrypted}`;
  };

  useEffect(() => {
    const loadStoredImages = async () => {
      const encryptedImages = messages.filter((message) => (
        message.messageType === 'image'
        && Boolean(finalKeyHex)
        && (Boolean(message.storagePath) || Boolean(message.storageUrl))
      ));

      if (encryptedImages.length === 0) return;

      const nextMap: Record<string, string> = {};

      await Promise.all(encryptedImages.map(async (message) => {
        try {
          const downloadUrl = message.storageUrl || await getDownloadURL(ref(storage, message.storagePath || ''));
          const encryptedPayload = await fetch(downloadUrl).then((response) => response.text());
          const decrypted = EncryptionService.decrypt(encryptedPayload, finalKeyHex);

          if (!decrypted || decrypted.includes('Decryption Error')) {
            return;
          }

          if (decrypted.startsWith('data:image/')) {
            nextMap[message.id] = decrypted;
            return;
          }

          if (message.mimeType) {
            nextMap[message.id] = `data:${message.mimeType};base64,${decrypted}`;
            return;
          }

          nextMap[message.id] = `data:image/png;base64,${decrypted}`;
        } catch (error) {
          console.error('Failed to load encrypted image payload:', error);
        }
      }));

      setDecryptedImageMap((previous) => ({ ...previous, ...nextMap }));
    };

    if (!finalKeyHex) {
      setDecryptedImageMap({});
      return;
    }

    loadStoredImages();
  }, [messages, finalKeyHex]);

  const previewImageSource = imageDraft?.compressedDataUrl || null;

  if (!authResolved) {
    return (
      <div className="chat-container">
        <div className="chat-header">
          <p style={{ color: '#ffffff' }}>Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="chat-container">
        <div className="chat-header">
          <p style={{ color: '#ffffff' }}>Please login again to continue chatting.</p>
        </div>
      </div>
    );
  }

  if (fetchingUser) {
    return (
      <div className="chat-container">
        <div className="chat-header">
          <p style={{ color: '#ffffff' }}>Loading user...</p>
        </div>
      </div>
    );
  }

  if (userId && !chatUser) {
    return (
      <div className="chat-container">
        <div className="chat-header">
          <p style={{ color: '#ffffff' }}>User not found</p>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="chat-container">
        <div className="chat-header">
          <p style={{ color: '#ffffff' }}>Open chat from the Users page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button className="back-button" onClick={handleBackClick}>←</button>
        <div className="chat-user-info">
          {chatUser?.photoURL && (
            <img src={chatUser.photoURL} alt={chatUser.displayName} className="chat-user-avatar" />
          )}
          {!chatUser?.photoURL && (
            <div className="chat-user-avatar-placeholder">
              {(chatUser?.displayName || chatUser?.email || 'User')[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <h1>{chatUser?.displayName || 'Anonymous'}</h1>
            <p className="chat-user-email">{chatUser?.email || ''}</p>
          </div>
        </div>
        <div className="quantum-panel">
          <div className="quantum-panel-header">
            <p className="quantum-panel-title">BB84 Handshake</p>
            <label className="quantum-toggle">
              <input
                type="checkbox"
                checked={eavesdropperActive}
                onChange={(e) => setEavesdropperActive(e.target.checked)}
                disabled={quantumLoading}
              />
              Eve
            </label>
          </div>
          <div className="quantum-progress">
            <div className="quantum-progress-line" />
            <div
              className="quantum-progress-line-active"
              style={{ width: `${(Math.max(quantumStep - 1, 0) / (QUANTUM_STEPS.length - 1)) * 100}%` }}
            />
            {QUANTUM_STEPS.map((label, index) => {
              const stepNumber = index + 1;
              const isCompleted = quantumStep > stepNumber;
              const isActive = quantumStep === stepNumber;

              return (
                <div
                  key={label}
                  className={`quantum-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`.trim()}
                >
                  <span className="quantum-step-dot" />
                  <span className="quantum-step-label">{label}</span>
                </div>
              );
            })}
          </div>
          <p className="quantum-status">{quantumStatus}</p>
          {quantumError && <p className="quantum-error">{quantumError}</p>}
          {finalKeyHex && (
            <>
              <p className="quantum-meta">Sifted bits: {matchingCount}</p>
              <p className="quantum-key">{finalKeyHex}</p>
            </>
          )}
          <div className="quantum-actions-row">
            <button
              className="quantum-btn"
              onClick={runQuantumHandshake}
              disabled={quantumLoading}
            >
              {quantumLoading ? 'Running...' : 'Run Key Exchange'}
            </button>
            <button
              className="quantum-btn quantum-btn-danger"
              onClick={abortQuantumHandshake}
              disabled={quantumLoading}
            >
              Abort
            </button>
          </div>
        </div>
      </div>

      <div className="chat-messages">
        {chatError && (
          <div className="chat-empty">
            <p>{chatError}</p>
          </div>
        )}
        {!chatError && messages.length === 0 ? (
          <div className="chat-empty">
            <p>Start a conversation</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`chat-message ${msg.senderId === currentUser?.uid ? 'user' : 'bot'}`}
            >
              <div className={`message-content ${msg.messageType === 'image' ? 'message-content-media' : ''}`.trim()}>
                {msg.messageType === 'image' ? (
                  (() => {
                    const imageSource = getMessageImageSource(msg);

                    if (!imageSource) {
                      const waitingForStorage = Boolean(msg.storagePath || msg.storageUrl);
                      return (
                        <p className="message-image-fallback">
                          {waitingForStorage
                            ? '[Encrypted image] Fetching and decrypting...'
                            : '[Encrypted image] Unable to decrypt with current key.'}
                        </p>
                      );
                    }

                    return (
                      <img
                        src={imageSource}
                        alt={msg.senderName || 'Encrypted image'}
                        className="chat-message-image"
                      />
                    );
                  })()
                ) : (
                  getMessageText(msg)
                )}
              </div>
              <div className="message-time">
                {msg.timestamp instanceof Timestamp
                  ? msg.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : msg.timestamp instanceof Date
                    ? msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : ''
                }
              </div>
            </div>
          ))
        )}
      </div>

      <div className="chat-input-container">
        {imageDraft && (
          <div className="image-preview-card">
            <div className="image-preview-header">
              <div>
                <p className="image-preview-title">Image preview</p>
                <p className="image-preview-meta">
                  {imageDraft.fileName} · {Math.round(imageDraft.originalSize / 1024)} KB · {imageDraft.compressedWidth}×{imageDraft.compressedHeight}
                </p>
              </div>
              <button className="image-preview-clear" type="button" onClick={clearImageDraft} disabled={imageSending}>
                Remove
              </button>
            </div>
            <img src={previewImageSource || ''} alt={imageDraft.fileName} className="image-preview-image" />
          </div>
        )}
        <div className="chat-input-row">
          <input
            type="text"
            className="chat-input"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
            disabled={loading || imageSending}
          />
          <button
            className="chat-image-btn"
            onClick={handleImageButtonClick}
            disabled={loading || imageSending || !finalKeyHex}
            type="button"
          >
            Image
          </button>
          <button
            className="chat-send-btn"
            onClick={imageDraft ? sendImageMessage : handleSendMessage}
            disabled={loading || imageSending || (!input.trim() && !imageDraft)}
            type="button"
          >
            {imageDraft ? (imageSending ? 'Sending...' : 'Send Image') : (loading ? '...' : 'Send')}
          </button>
        </div>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          className="chat-image-input"
          onChange={handleImageSelected}
        />
      </div>
    </div>
  );
};

export default Chat;
